import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { computeStaticPeaksRmsStereo, type StereoPeakRms } from '@/features/services/media/components/computeStaticPeaks';
import { useSidecarMeta, type BeatEvent } from '@/hooks/useSidecarMeta';
import { bff } from '@/lib/http/bff';
import {
  applyPlaybackRate,
  applyPreservesPitch,
  clampPlaybackRate,
  PLAYBACK_RATE_STOPS,
  stepPlaybackRate,
} from '@/features/services/media/hooks/timeStretch';

function formatDurationSeconds(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function safeEncodeURIComponent(v: string): string {
  try { return encodeURIComponent(v); } catch { return v; }
}

let decodeCtx: AudioContext | null = null;
const peaksCache = new Map<string, StereoPeakRms>();
const peaksInflight = new Map<string, Promise<StereoPeakRms>>();
let activeAudioEl: HTMLAudioElement | null = null;
let lastActiveAudioEl: HTMLAudioElement | null = null;
const WAVE_ZOOM_STOPS = [1, 2, 4, 8] as const;

function getDecodeCtx(): AudioContext {
  if (decodeCtx && decodeCtx.state !== 'closed') return decodeCtx;
  const C = (window as any).AudioContext || (window as any).webkitAudioContext;
  decodeCtx = new C() as AudioContext;
  return decodeCtx!;
}

async function resolveWaveformPeaks(src: string, signal?: AbortSignal): Promise<StereoPeakRms> {
  const cached = peaksCache.get(src);
  if (cached) return cached;

  const inflight = peaksInflight.get(src);
  if (inflight) return await inflight;

  const p = (async () => {
    let lastErr: any = null;
    const fetchPlans: Array<{ cache: RequestCache; label: string }> = [
      { cache: 'force-cache', label: 'cache' },
      { cache: 'no-store', label: 'no-store' },
      { cache: 'no-store', label: 'no-store-retry' },
    ];
    for (let attempt = 0; attempt < fetchPlans.length; attempt++) {
      try {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const plan = fetchPlans[attempt];

        const res = await fetch(src, {
          mode: 'cors',
          cache: plan.cache,
          headers: { Accept: 'audio/*,*/*;q=0.8' },
          signal,
        });
        if (!res.ok) {
          if (attempt < fetchPlans.length - 1 && (res.status === 502 || res.status === 503 || res.status === 504)) {
            await new Promise((r) => setTimeout(r, 250 * Math.pow(2, attempt)));
            continue;
          }
          throw new Error(`HTTP ${res.status} (${plan.label})`);
        }

        const ct = res.headers.get('content-type') || '';
        if (ct && !/audio\//i.test(ct) && !/octet-stream/i.test(ct)) {
          const snippet = (await res.text().catch(() => '')).slice(0, 120);
          throw new Error(`Unexpected content-type '${ct}' (${plan.label})${snippet ? ` :: ${snippet}` : ''}`);
        }

        // Partial content often cannot be fully decoded to static peaks.
        if (res.status === 206) {
          throw new Error(`Partial content 206 (${plan.label})`);
        }

        const arr = await res.arrayBuffer();
        const buf = await getDecodeCtx().decodeAudioData(arr);
        const cols = Math.max(512, Math.min(4096, Math.round(buf.duration * 120)));
        const peaks = computeStaticPeaksRmsStereo(buf, cols);
        peaksCache.set(src, peaks);
        peaksInflight.delete(src);
        return peaks;
      } catch (e: any) {
        lastErr = e;
        if (e?.name === 'AbortError') throw e;
        if (attempt < fetchPlans.length - 1) await new Promise((r) => setTimeout(r, 300 * Math.pow(2, attempt)));
      }
    }
    throw lastErr ?? new Error('waveform_decode_failed');
  })();

  peaksInflight.set(src, p);
  p.catch(() => peaksInflight.delete(src));
  return await p;
}

function snapToNearestBeat(rawSeconds: number, duration: number | null, beatEvents: BeatEvent[], enabled: boolean): number {
  const max = duration ?? Number.POSITIVE_INFINITY;
  const t = Math.max(0, Math.min(max, rawSeconds));
  if (!enabled || beatEvents.length === 0) return t;

  let closest = t;
  let minDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < beatEvents.length; i++) {
    const bt = Number(beatEvents[i].t);
    if (!Number.isFinite(bt)) continue;
    const d = Math.abs(bt - t);
    if (d < minDist) {
      minDist = d;
      closest = bt;
    }
  }
  return Math.max(0, Math.min(max, closest));
}

export interface OutcomeTrackLineAudioPlayerProps {
  gsUri: string;
  /**
   * When true, enables DAW-style loop-region markers:
   *   - Press 'm' while this track is playing → alternates setting IN / OUT at playhead
   *   - Press 'c' or Esc → clears markers
   *   - When both IN+OUT set, playback auto-loops between them.
   * Renders two vertical guides + a shaded region on the waveform.
   * Default false (Releases / inline players unaffected).
   */
  enableMarkers?: boolean;
  /**
   * When true, enables DAW-style time-stretch (varispeed with pitch preserved):
   *   - Stops: 0.5× / 0.75× / 1× / 1.25× / 1.5× / 1.75× / 2×
   *   - Keyboard '[' decreases, ']' increases, '0' resets to 1× (only while playing).
   *   - `preservesPitch` is set on the underlying <audio> element.
   * Default false.
   */
  enableSpeed?: boolean;
  /**
   * When true, renders the beat-grid (vertical lines per beat, thicker per
   * downbeat) over the waveform when the audio sidecar exposes `analysis.beats`.
   * If `scopeKey` is also provided, a “Detect beats” button is shown when no
   * grid is present yet — it triggers the on-demand BeatNet DAG and the FE
   * polls the sidecar until the analysis slot appears.
   * Default false.
   */
  enableBeatGrid?: boolean;
  /**
   * scope_key of this audio entry (NS_YOUTUBE / audio library). Required to
   * trigger on-demand analysis lenses. Without it, beat-grid is read-only.
   */
  scopeKey?: string;
  /**
    * When true, renders the waveform at a taller height (h-40 instead of h-14).
   * Useful for a focused / detail-view player.
   */
  tall?: boolean;
  /**
   * Pass `true` while the BFF has auto-dispatched post-ingest analysis
   * (BPM + beat detection) and results have not yet appeared in the sidecar.
   * The player replaces the manual "detect beats" button with a pulsing
   * "analyzing…" indicator so the user knows the pipeline is already running.
   * Automatically becomes irrelevant once sidecar.analysis.beats arrives
   * (hasBeatGrid turns true and the beat-grid renders).
   */
  analysisInProgress?: boolean;
  /**
   * Optional: notified with the underlying `<audio>` element on mount and
   * `null` on unmount. Used by the parent to attach a live Web Audio
   * analyser graph (e.g. spectrum analyzer) without coupling the player
   * to a specific visualization. Stable identity expected (useCallback).
   */
  onAudioElement?: (el: HTMLAudioElement | null) => void;
  /**
   * Called synchronously inside the play button handler before `el.play()`.
   * Use this to resume a suspended AudioContext in the same user-gesture
   * call stack — the only reliable way to satisfy Chrome's autoplay policy.
   */
  onResumeContext?: () => void;
}

export default function OutcomeTrackLineAudioPlayer({
  gsUri,
  enableMarkers = false,
  enableSpeed = false,
  enableBeatGrid = false,
  scopeKey,
  tall = false,
  analysisInProgress = false,
  onAudioElement,
  onResumeContext,
}: OutcomeTrackLineAudioPlayerProps) {
  const playerRootRef = useRef<HTMLDivElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const handleAudioRef = useCallback((el: HTMLAudioElement | null) => {
    audioRef.current = el;
    try { onAudioElement?.(el); } catch {}
  }, [onAudioElement]);

  // Sidecar metadata: progressive enhancement (gracefully null for old artifacts).
  const { data: sidecar } = useSidecarMeta(gsUri);
  const queryClient = useQueryClient();

  /* ── Beat-grid lens (sidecar v2 / analysis.beats) ───────────── */
  const beats = useMemo<BeatEvent[]>(() => {
    if (!enableBeatGrid) return [];
    const arr = sidecar?.analysis?.beats;
    return Array.isArray(arr) ? arr : [];
  }, [enableBeatGrid, sidecar]);
  const hasBeatGrid = beats.length > 0;
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  /**
   * Live progress for the beat-detection run, sourced from the orchestrator
   * SSE snapshot stream (/bff/orchestrator/runs/:run_id/stream). Surfacing
   * the actual step state + per-step `last_error` is the difference between
   * “err” (unhelpful) and “step `analyze_beats` failed: out of memory at attempt 2”.
   */
  type AnalyzeStep = {
    step_id: string;
    status: string;
    attempts?: number | null;
    last_error?: string | null;
    progress?: { percent?: number | null; message?: string | null } | null;
    started_at?: string | null;
    finished_at?: string | null;
    duration_ms?: number | null;
  };
  type AnalyzeProgress = {
    runId: string;
    status: string;
    percent: number;
    counts: { total: number; succeeded: number; failed: number; running: number; pending: number; ready: number };
    steps: AnalyzeStep[];
    startedAt: number;
    streamError?: string | null;
  };
  const [analyzeProgress, setAnalyzeProgress] = useState<AnalyzeProgress | null>(null);
  const analyzePollRef = useRef<number | null>(null);
  const analyzeStreamRef = useRef<EventSource | null>(null);

  // Cleanup any pending poll/stream on unmount or src change.
  useEffect(() => () => {
    if (analyzePollRef.current != null) {
      window.clearTimeout(analyzePollRef.current);
      analyzePollRef.current = null;
    }
    try { analyzeStreamRef.current?.close(); } catch {}
    analyzeStreamRef.current = null;
  }, [gsUri]);

  const sidecarQueryKey = useMemo(
    () => ['buckets', 'sidecarMeta', gsUri ? `${gsUri}.meta.json` : ''] as const,
    [gsUri],
  );

  const requestBeatDetection = useCallback(async () => {
    if (!enableBeatGrid || !scopeKey || isAnalyzing || hasBeatGrid) return;
    setAnalyzeError(null);
    setAnalyzeProgress(null);
    setIsAnalyzing(true);

    // 1. Dispatch the analysis run.
    let runId: string | null = null;
    try {
      const res = await bff.post<{ run_id?: string; trace_id?: string }>(
        `/media-v2/audio-library/${encodeURIComponent(scopeKey)}/analyze`,
        { kind: 'beats' },
      );
      runId = res?.run_id ? String(res.run_id) : null;
    } catch (e: any) {
      setIsAnalyzing(false);
      const detail = e?.body?.error || e?.body?.message || e?.message || 'analysis_dispatch_failed';
      setAnalyzeError(`Could not start beat detection: ${detail}`);
      return;
    }

    const startedAt = Date.now();
    setAnalyzeProgress({
      runId: runId ?? '',
      status: 'PENDING',
      percent: 0,
      counts: { total: 0, succeeded: 0, failed: 0, running: 0, pending: 0, ready: 0 },
      steps: [],
      startedAt,
    });

    // 2. Subscribe to the orchestrator run SSE for live, detailed progress.
    if (runId) {
      try { analyzeStreamRef.current?.close(); } catch {}
      const streamUrl = `/bff/orchestrator/runs/${encodeURIComponent(runId)}/stream?poll_ms=1500`;
      const es = new EventSource(streamUrl, { withCredentials: true });
      analyzeStreamRef.current = es;

      const handleSnapshot = (raw: string) => {
        try {
          const snap = JSON.parse(raw);
          const status = String(snap?.status ?? 'RUNNING').toUpperCase();
          const percent = Number.isFinite(snap?.percent) ? Number(snap.percent) : 0;
          const counts = snap?.counts ?? { total: 0, succeeded: 0, failed: 0, running: 0, pending: 0, ready: 0 };
          const steps: AnalyzeStep[] = Array.isArray(snap?.steps) ? snap.steps : [];
          setAnalyzeProgress((prev) => ({
            runId: runId!,
            status,
            percent,
            counts,
            steps,
            startedAt: prev?.startedAt ?? startedAt,
            streamError: null,
          }));
          if (status === 'FAILED' || status === 'ERROR' || counts?.failed > 0) {
            const failedStep = steps.find((s) => {
              const st = String(s.status || '').toUpperCase();
              return st === 'FAILED' || st === 'ERROR' || !!s.last_error;
            });
            const detail = failedStep?.last_error
              ? `step \`${failedStep.step_id}\` failed: ${failedStep.last_error}`
              : `Beat detection failed (run ${runId}).`;
            setAnalyzeError(detail);
          }
        } catch { /* ignore malformed event */ }
      };

      es.addEventListener('snapshot', (ev: MessageEvent) => handleSnapshot(ev.data));
      es.addEventListener('done', (ev: MessageEvent) => {
        try {
          const data = JSON.parse(ev.data ?? '{}');
          const st = String(data?.status ?? '').toUpperCase();
          if (st && st !== 'SUCCEEDED') {
            setAnalyzeError((prev) => prev ?? `Beat detection ended with status ${st}`);
          }
        } catch {}
        try { es.close(); } catch {}
        analyzeStreamRef.current = null;
      });
      es.addEventListener('server_error', (ev: MessageEvent) => {
        try {
          const data = JSON.parse(ev.data ?? '{}');
          setAnalyzeProgress((prev) => prev ? { ...prev, streamError: String(data?.error ?? 'stream_error') } : prev);
        } catch {}
      });
      es.onerror = () => {
        // Browser auto-reconnects; just surface a soft hint.
        setAnalyzeProgress((prev) => prev ? { ...prev, streamError: 'progress stream disconnected — reconnecting…' } : prev);
      };
    }

    // 3. Poll the sidecar until analysis.beats appears (worker writes it on success).
    //    Backoff: 2s base, capped at 5s, abandon after ~3 min.
    const tick = async () => {
      if (Date.now() - startedAt > 180_000) {
        setIsAnalyzing(false);
        setAnalyzeError((prev) => prev ?? 'Beat detection timed out after 3 minutes (worker may be unavailable).');
        try { analyzeStreamRef.current?.close(); } catch {}
        analyzeStreamRef.current = null;
        return;
      }
      try {
        await queryClient.invalidateQueries({ queryKey: sidecarQueryKey });
        const fresh = queryClient.getQueryData(sidecarQueryKey) as { analysis?: { beats?: BeatEvent[] } } | null | undefined;
        if (fresh?.analysis?.beats && fresh.analysis.beats.length > 0) {
          setIsAnalyzing(false);
          setAnalyzeProgress(null);
          try { analyzeStreamRef.current?.close(); } catch {}
          analyzeStreamRef.current = null;
          return;
        }
      } catch { /* swallow transient errors and keep polling */ }
      const elapsed = Date.now() - startedAt;
      const delay = elapsed < 15_000 ? 2_000 : elapsed < 60_000 ? 3_500 : 5_000;
      analyzePollRef.current = window.setTimeout(tick, delay);
    };
    analyzePollRef.current = window.setTimeout(tick, 2_500);
  }, [enableBeatGrid, scopeKey, isAnalyzing, hasBeatGrid, queryClient, sidecarQueryKey]);

  /* ── Marker state (DAW-style loop region) ──────────────────── */
  const [markerIn, setMarkerIn] = useState<number | null>(null);
  const [markerOut, setMarkerOut] = useState<number | null>(null);
  /** Next slot that 'm' will write into. Alternates IN → OUT → IN → … */
  const nextSlotRef = useRef<'in' | 'out'>('in');
  const markerInRef = useRef<number | null>(null);
  const markerOutRef = useRef<number | null>(null);
  useEffect(() => { markerInRef.current = markerIn; }, [markerIn]);
  useEffect(() => { markerOutRef.current = markerOut; }, [markerOut]);

  const clearMarkers = useCallback(() => {
    setMarkerIn(null);
    setMarkerOut(null);
    nextSlotRef.current = 'in';
  }, []);

  /** Sorted [lo, hi] view of the current loop region (or null if not both set). */
  const loopRegion = useMemo<[number, number] | null>(() => {
    if (markerIn == null || markerOut == null) return null;
    if (markerIn === markerOut) return null;
    const lo = Math.min(markerIn, markerOut);
    const hi = Math.max(markerIn, markerOut);
    return [lo, hi];
  }, [markerIn, markerOut]);

  /** Drag-selected loop region (takes precedence over marker-based loopRegion). */
  const [dragLoopRegion, setDragLoopRegion] = useState<[number, number] | null>(null);

  const activeLoopRegion = useMemo<[number, number] | null>(() => {
    if (dragLoopRegion) return dragLoopRegion;
    return loopRegion;
  }, [dragLoopRegion, loopRegion]);
  const activeLoopRegionRef = useRef<[number, number] | null>(null);
  useEffect(() => {
    activeLoopRegionRef.current = activeLoopRegion;
  }, [activeLoopRegion]);

  const [dragSnapMode, setDragSnapMode] = useState<'beat' | 'free'>('beat');

  /* ── Speed / time-stretch state (DAW-style) ───────────────── */
  const [speed, setSpeed] = useState<number>(1.0);
  const speedRef = useRef<number>(1.0);
  useEffect(() => { speedRef.current = speed; }, [speed]);

  /** Re-apply rate + preservesPitch to the audio element on every change
   *  AND on every track-load (the new <audio> defaults to 1.0 / undefined). */
  useEffect(() => {
    if (!enableSpeed) return;
    const el = audioRef.current;
    if (!el) return;
    applyPreservesPitch(el, true);
    applyPlaybackRate(el, speed);
  }, [enableSpeed, speed, gsUri]);

  function tryPlay(el: HTMLAudioElement) {
    // Resume any captured AudioContext synchronously within the gesture stack.
    try { onResumeContext?.(); } catch {}
    try {
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err: unknown) => {
          const name = (err as { name?: string } | null)?.name;
          if (name !== 'AbortError') {
            try { console.warn('[OutcomeTrackLineAudioPlayer] play() failed', err); } catch {}
          }
        });
      }
    } catch {}
  }

  const src = useMemo(() => {
    const u = String(gsUri || '').trim();
    if (!u) return '';
    return `/bff/gcs/stream?gsUri=${safeEncodeURIComponent(u)}`;
  }, [gsUri]);

  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  /** Triple-tap gesture unlocks loop mode. Normal clicks = play/pause toggle. */
  const [looping, setLooping] = useState(false);
  const loopingRef = useRef(false);
  useEffect(() => { loopingRef.current = looping; }, [looping]);
  /** Tap timestamps for triple-tap detection (kept within 750ms sliding window). */
  const tapTimesRef = useRef<number[]>([]);
  const [duration, setDuration] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [waveZoom, setWaveZoom] = useState<number>(1);
  const [trackHasFocus, setTrackHasFocus] = useState(false);
  const [hoverTimeUi, setHoverTimeUi] = useState<{ x: number; sec: number } | null>(null);
  const [dragPreviewRegion, setDragPreviewRegion] = useState<[number, number] | null>(null);
  const dragStartXRef = useRef<number | null>(null);
  const dragStartSecRef = useRef<number | null>(null);
  const dragMovedRef = useRef<boolean>(false);
  const suppressClickSeekRef = useRef<boolean>(false);
  const dragPreviewRafRef = useRef<number | null>(null);
  const dragPreviewNextRef = useRef<[number, number] | null>(null);
  const panRafRef = useRef<number | null>(null);
  const panNextTimeRef = useRef<number | null>(null);
  const hoverUiRafRef = useRef<number | null>(null);
  const hoverUiNextRef = useRef<{ x: number; sec: number } | null>(null);
  const hoverAnchorSecRef = useRef<number | null>(null);
  const resizeEdgeRef = useRef<'left' | 'right' | null>(null);
  const handleHitDistanceRef = useRef<number>(8); // pixels to detect handle hover

  const zoomWindow = useMemo(() => {
    if (!duration || !Number.isFinite(duration) || duration <= 0) {
      return { start: 0, end: 0, span: 0 };
    }
    const zoom = Math.max(1, waveZoom);
    const span = duration / zoom;
    if (span >= duration) return { start: 0, end: duration, span: duration };
    const half = span / 2;
    const centered = currentTime - half;
    const start = Math.max(0, Math.min(duration - span, centered));
    const end = start + span;
    return { start, end, span };
  }, [duration, currentTime, waveZoom]);

  const setZoomAround = useCallback((anchorSec: number, nextZoom: number) => {
    if (!duration || !Number.isFinite(duration) || duration <= 0) return;
    const clampedAnchor = Math.max(0, Math.min(duration, anchorSec));
    const clampedZoom = Math.max(WAVE_ZOOM_STOPS[0], Math.min(WAVE_ZOOM_STOPS[WAVE_ZOOM_STOPS.length - 1], nextZoom));
    setCurrentTime(clampedAnchor);
    setWaveZoom(clampedZoom);
  }, [duration]);

  const getZoomAnchorSec = useCallback(() => {
    if (hoverAnchorSecRef.current != null) return hoverAnchorSecRef.current;
    return currentTime || 0;
  }, [currentTime]);

  const shiftWaveZoom = useCallback((dir: -1 | 1) => {
    const idx = Math.max(0, WAVE_ZOOM_STOPS.indexOf(waveZoom as (typeof WAVE_ZOOM_STOPS)[number]));
    const nextIdx = Math.max(0, Math.min(WAVE_ZOOM_STOPS.length - 1, idx + dir));
    const anchor = getZoomAnchorSec();
    setZoomAround(anchor, WAVE_ZOOM_STOPS[nextIdx]);
  }, [waveZoom, getZoomAnchorSec, setZoomAround]);

  const fitWaveSelection = useCallback(() => {
    if (!duration || !Number.isFinite(duration) || duration <= 0) return;
    const region = dragLoopRegion ?? loopRegion;
    if (!region) {
      setZoomAround(getZoomAnchorSec(), 1);
      return;
    }
    const [lo, hi] = region;
    const span = Math.max(hi - lo, 0.05);
    const targetZoom = Math.max(1, Math.min(WAVE_ZOOM_STOPS[WAVE_ZOOM_STOPS.length - 1], duration / span));
    const anchor = lo + span / 2;
    setZoomAround(anchor, targetZoom);
  }, [duration, dragLoopRegion, loopRegion, getZoomAnchorSec, setZoomAround]);

  // Pre-set duration from sidecar if the <audio> element hasn't loaded metadata yet.
  useEffect(() => {
    if (duration == null && sidecar?.duration_ms != null && sidecar.duration_ms > 0) {
      setDuration(sidecar.duration_ms / 1000);
    }
  }, [sidecar, duration]);

  /** Compact label from sidecar metadata (e.g. "Artist — Title"). */
  const sidecarLabel = useMemo(() => {
    if (!sidecar) return null;
    const parts: string[] = [];
    if (sidecar.artist) parts.push(sidecar.artist);
    if (sidecar.title) parts.push(sidecar.title);
    return parts.length > 0 ? parts.join(' — ') : null;
  }, [sidecar]);
  const [hasError, setHasError] = useState(false);
  const [wavePeaks, setWavePeaks] = useState<StereoPeakRms | null>(null);
  const [waveError, setWaveError] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const copyTimerRef = useRef<number | null>(null);

  const playbackStatusLabel = useMemo(() => {
    if (isPlaying) return 'Playing';
    if (currentTime > 0) return `Paused @ ${formatDurationSeconds(currentTime)}`;
    return 'Stopped';
  }, [currentTime, isPlaying]);

  const progressPct = useMemo(() => {
    if (!duration || !Number.isFinite(duration) || duration <= 0) return 0;
    return Math.max(0, Math.min(100, (currentTime / duration) * 100));
  }, [duration, currentTime]);

  const remainingLabel = useMemo(() => {
    if (!duration || !Number.isFinite(duration)) return '—';
    return `-${formatDurationSeconds(Math.max(0, duration - currentTime))}`;
  }, [duration, currentTime]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    setCopyState('idle');
    if (copyTimerRef.current != null) {
      window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = null;
    }

    setIsReady(false);
    setIsPlaying(false);
    setLooping(false);
    loopingRef.current = false;
    tapTimesRef.current = [];
    setDuration(null);
    setCurrentTime(0);
    setWaveZoom(1);
    setDragLoopRegion(null);
    setDragPreviewRegion(null);
    dragStartXRef.current = null;
    dragStartSecRef.current = null;
    dragMovedRef.current = false;
    suppressClickSeekRef.current = false;
    setHasError(false);
    setWavePeaks(null);
    setWaveError(false);
    // Reset markers whenever the source track changes.
    setMarkerIn(null);
    setMarkerOut(null);
    nextSlotRef.current = 'in';

    const onLoadedMetadata = () => {
      const d = Number.isFinite(el.duration) ? el.duration : NaN;
      setDuration(Number.isFinite(d) ? d : null);
      setIsReady(true);
      // Re-apply current speed + pitch-preserve on every fresh load
      // (browsers reset playbackRate on src change). DRY via shared helpers.
      if (enableSpeed) {
        applyPreservesPitch(el, true);
        applyPlaybackRate(el, speedRef.current);
      }
    };
    const onDurationChange = () => {
      const d = Number.isFinite(el.duration) ? el.duration : NaN;
      setDuration(Number.isFinite(d) ? d : null);
    };
    const onTimeUpdate = () => setCurrentTime(el.currentTime || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => {
      setIsPlaying(false);
      if (activeAudioEl === el) activeAudioEl = null;
    };
    const onEnded = () => {
      const region = activeLoopRegionRef.current;
      if (region) {
        try { el.currentTime = region[0]; } catch {}
        tryPlay(el);
        return;
      }
      // If looping, restart immediately with 0 delay.
      if (loopingRef.current) {
        try { el.currentTime = 0; } catch {}
        tryPlay(el);
        return;
      }
      // Natural end: stop and reset.
      setIsPlaying(false);
      try { el.currentTime = 0; } catch {}
      setCurrentTime(0);
      if (activeAudioEl === el) activeAudioEl = null;
    };
    const onError = () => {
      setHasError(true);
      setIsReady(false);
      setIsPlaying(false);
      setDuration(null);
    };

    el.addEventListener('loadedmetadata', onLoadedMetadata);
    el.addEventListener('durationchange', onDurationChange);
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onError);

    return () => {
      el.removeEventListener('loadedmetadata', onLoadedMetadata);
      el.removeEventListener('durationchange', onDurationChange);
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
    };
  }, [src]);

  useEffect(() => {
    if (!src) return;
    let alive = true;
    setWaveError(false);
    setWavePeaks(null);

    void (async () => {
      try {
        const peaks = await resolveWaveformPeaks(src);
        if (!alive) return;
        setWavePeaks(peaks);
      } catch (err: any) {
        if (!alive) return;
        if ((err?.name || '') !== 'AbortError') {
          try { console.warn('[OutcomeTrackLineAudioPlayer] static waveform decode failed', src, err); } catch {}
        }
        setWaveError(true);
        setWavePeaks(null);
      }
    })();

    return () => { alive = false; };
  }, [src]);

  useEffect(() => {
    const c = canvasRef.current;
    const track = trackRef.current;
    if (!c || !track) return;

    const sizeCanvas = () => {
      const cssW = Math.max(1, Math.floor(track.clientWidth));
      const cssH = Math.max(1, Math.floor(track.clientHeight));
      const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
      c.style.width = `${cssW}px`;
      c.style.height = `${cssH}px`;
      c.width = Math.max(1, Math.floor(cssW * dpr));
      c.height = Math.max(1, Math.floor(cssH * dpr));
      const ctx = c.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    sizeCanvas();
    const ro = ('ResizeObserver' in window) ? new ResizeObserver(sizeCanvas) : null;
    if (ro) ro.observe(track);
    window.addEventListener('resize', sizeCanvas);
    return () => {
      window.removeEventListener('resize', sizeCanvas);
      try { ro?.disconnect(); } catch {}
    };
  }, []);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;

    const W = Math.max(1, Math.floor(c.clientWidth));
    const H = Math.max(1, Math.floor(c.clientHeight));
    const mid = H / 2;
    const hasDuration = !!duration && Number.isFinite(duration) && duration > 0;
    const viewStart = hasDuration ? zoomWindow.start : 0;
    const viewEnd = hasDuration ? zoomWindow.end : 0;
    const viewSpan = hasDuration ? Math.max(1e-6, zoomWindow.span) : 0;

    const timeToX = (t: number): number => {
      if (!hasDuration) return 0;
      const ratio = (t - viewStart) / viewSpan;
      return Math.max(0, Math.min(W, ratio * W));
    };

    const isInView = (t: number): boolean => hasDuration && t >= viewStart && t <= viewEnd;

    ctx.clearRect(0, 0, W, H);

    // subtle center line
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, mid + 0.5);
    ctx.lineTo(W, mid + 0.5);
    ctx.stroke();

    if (wavePeaks && wavePeaks.left.length > 0) {
      const drawLane = (
        lane: StereoPeakRms['left'],
        centerY: number,
        amp: number,
        rmsColor: string,
        peakColor: string,
      ) => {
        const srcLen = lane.length;
        if (srcLen <= 0) return;

        let srcStart = 0;
        let srcSpan = srcLen;
        if (hasDuration) {
          const maxIdx = Math.max(0, srcLen - 1);
          srcStart = Math.max(0, Math.min(maxIdx, Math.floor((viewStart / duration!) * maxIdx)));
          const srcEnd = Math.max(srcStart + 1, Math.min(maxIdx, Math.ceil((viewEnd / duration!) * maxIdx)));
          srcSpan = Math.max(1, srcEnd - srcStart);
        }

        const indexForX = (x: number) => {
          const rel = Math.max(0, Math.min(1, x / Math.max(1, W - 1)));
          const idx = srcStart + Math.floor(rel * srcSpan);
          return Math.max(0, Math.min(srcLen - 1, idx));
        };

        ctx.strokeStyle = rmsColor;
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let x = 0; x < W; x++) {
          const idx = indexForX(x);
          const p = lane[idx];
          const rh = Math.max(0.35, p.rms * amp);
          ctx.moveTo(x, centerY - rh);
          ctx.lineTo(x, centerY + rh);
        }
        ctx.stroke();

        ctx.strokeStyle = peakColor;
        ctx.beginPath();
        for (let x = 0; x < W; x++) {
          const idx = indexForX(x);
          const p = lane[idx];
          const y1 = centerY + p.min * amp;
          const y2 = centerY + p.max * amp;
          ctx.moveTo(x, y1);
          ctx.lineTo(x, y2);
        }
        ctx.stroke();
      };

      if (wavePeaks.stereo && H >= 22) {
        const quarter = H / 4;
        const laneAmp = H * 0.19;
        drawLane(wavePeaks.left, quarter, laneAmp, 'rgba(125,211,252,0.38)', 'rgba(186,230,253,0.86)');
        drawLane(wavePeaks.right, quarter * 3, laneAmp, 'rgba(251,146,60,0.35)', 'rgba(254,215,170,0.84)');

        ctx.fillStyle = 'rgba(255,255,255,0.35)';
        ctx.font = '10px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';
        ctx.textBaseline = 'top';
        ctx.fillText('L', 4, 2);
        ctx.fillText('R', 4, Math.max(2, H - 12));
      } else {
        const amp = H * 0.43;
        drawLane(wavePeaks.left, mid, amp, 'rgba(163,163,163,0.42)', 'rgba(229,229,229,0.82)');
      }
    } else {
      // Static-only fallback lane when decode is not available.
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fillRect(0, Math.max(0, mid - 1), W, 2);
    }

    // played region dark overlay
    const playedW = hasDuration ? timeToX(currentTime) : Math.max(0, Math.min(W, (progressPct / 100) * W));
    if (playedW > 0) {
      ctx.fillStyle = 'rgba(0,0,0,0.28)';
      ctx.fillRect(0, 0, playedW, H);
    }

    // time grid overlay
    ctx.fillStyle = 'rgba(255,255,255,0.02)';
    for (let x = 0; x < W; x += 12) {
      ctx.fillRect(x, 0, 1, H);
    }

    // playhead
    const px = hasDuration
      ? Math.max(0, Math.min(W - 1, timeToX(currentTime)))
      : Math.max(0, Math.min(W - 1, (progressPct / 100) * W));
    if (isPlaying) {
      ctx.fillStyle = 'rgba(245,245,245,0.9)';
      ctx.fillRect(px, 0, 1, H);
    } else {
      ctx.fillStyle = 'rgba(74,222,128,0.95)';
      ctx.fillRect(px, 0, 2, H);
      ctx.fillStyle = 'rgba(74,222,128,0.28)';
      ctx.fillRect(Math.max(0, px - 1), 0, 4, H);
      ctx.fillStyle = 'rgba(134,239,172,0.95)';
      ctx.fillRect(px, 0, 2, 4);
    }

    // beat-grid overlay (sidecar v2 — analysis.beats)
    if (enableBeatGrid && beats.length > 0 && hasDuration) {
      ctx.fillStyle = 'rgba(163,163,163,0.38)';   // neutral gray beats
      for (let i = 0; i < beats.length; i++) {
        const ev = beats[i];
        if (ev.type === 'downbeat') continue;
        const t = Number(ev.t);
        if (!Number.isFinite(t) || !isInView(t)) continue;
        const x = Math.max(0, Math.min(W - 1, timeToX(t)));
        ctx.fillRect(x, 0, 1, H);
      }
      ctx.fillStyle = 'rgba(212,212,212,0.72)'; // lighter gray for downbeats
      for (let i = 0; i < beats.length; i++) {
        const ev = beats[i];
        if (ev.type !== 'downbeat') continue;
        const t = Number(ev.t);
        if (!Number.isFinite(t) || !isInView(t)) continue;
        const x = Math.max(0, Math.min(W - 2, timeToX(t)));
        ctx.fillRect(x, 0, 2, H);
      }
    }

    if (hasDuration) {
      const region = dragPreviewRegion ?? dragLoopRegion;
      if (region) {
        const [lo, hi] = region;
        const xLo = Math.max(0, Math.min(W, timeToX(lo)));
        const xHi = Math.max(0, Math.min(W, timeToX(hi)));
        if (xHi > xLo) {
          ctx.fillStyle = dragPreviewRegion ? 'rgba(34,197,94,0.18)' : 'rgba(34,197,94,0.13)';
          ctx.fillRect(xLo, 0, xHi - xLo, H);
          
          // Draw left handle (circle)
          ctx.fillStyle = 'rgba(74,222,128,1)';
          const handleRadius = 5;
          ctx.beginPath();
          ctx.arc(xLo, H / 2, handleRadius, 0, Math.PI * 2);
          ctx.fill();
          
          // Draw right handle (circle)
          ctx.beginPath();
          ctx.arc(xHi, H / 2, handleRadius, 0, Math.PI * 2);
          ctx.fill();
          
          // Draw edge lines
          ctx.fillStyle = 'rgba(74,222,128,0.9)';
          ctx.fillRect(xLo, 0, 2, H);
          ctx.fillRect(Math.max(0, xHi - 2), 0, 2, H);
        }
      }
    }

    // marker region (only when enabled)
    if (enableMarkers && hasDuration) {
      const xFor = (t: number) => Math.max(0, Math.min(W, timeToX(t)));
      // Shaded region between IN and OUT
      if (loopRegion) {
        const [lo, hi] = loopRegion;
        const xLo = xFor(lo);
        const xHi = xFor(hi);
        if (xHi > xLo) {
          ctx.fillStyle = 'rgba(168,85,247,0.16)'; // violet tint
          ctx.fillRect(xLo, 0, xHi - xLo, H);
        }
      }
      // IN marker (cyan)
      if (markerIn != null) {
        const x = xFor(markerIn);
        ctx.fillStyle = 'rgba(34,211,238,0.95)';
        ctx.fillRect(x, 0, 2, H);
        ctx.fillStyle = 'rgba(34,211,238,0.95)';
        ctx.fillRect(x, 0, 6, 4);
      }
      // OUT marker (amber)
      if (markerOut != null) {
        const x = xFor(markerOut);
        ctx.fillStyle = 'rgba(251,191,36,0.95)';
        ctx.fillRect(Math.max(0, x - 1), 0, 2, H);
        ctx.fillStyle = 'rgba(251,191,36,0.95)';
        ctx.fillRect(Math.max(0, x - 5), 0, 6, 4);
      }
    }
  }, [wavePeaks, waveError, progressPct, enableMarkers, markerIn, markerOut, loopRegion, duration, enableBeatGrid, beats, dragLoopRegion, dragPreviewRegion, zoomWindow, currentTime]);

  /* ── Loop enforcement: seek IN when playhead crosses OUT ───── */
  useEffect(() => {
    if (!enableMarkers && !dragLoopRegion) return;
    if (!activeLoopRegion) return;
    const el = audioRef.current;
    if (!el) return;
    const [lo, hi] = activeLoopRegion;
    if (currentTime >= hi) {
      try { el.currentTime = lo; } catch {}
    } else if (currentTime < lo) {
      // If playhead is before IN while loop is active, jump to IN to enter the region.
      try { el.currentTime = lo; } catch {}
    }
  }, [enableMarkers, currentTime, activeLoopRegion, dragLoopRegion]);

  /* ── Keyboard: 'm' adds/alternates IN-OUT; 'c'/Esc clears ──── */
  useEffect(() => {
    if (!enableMarkers) return;
    if (!isPlaying) return; // Only the actively-playing track owns the keyboard.
    const onKey = (ev: KeyboardEvent) => {
      // Ignore when typing in inputs/textareas.
      const tgt = ev.target as HTMLElement | null;
      const tag = (tgt?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (tgt && tgt.isContentEditable)) return;
      const el = audioRef.current;
      if (!el) return;
      const key = ev.key.toLowerCase();
      if (key === 'm') {
        ev.preventDefault();
        const t = el.currentTime || 0;
        if (nextSlotRef.current === 'in') {
          setMarkerIn(t);
          nextSlotRef.current = 'out';
        } else {
          setMarkerOut(t);
          nextSlotRef.current = 'in';
        }
      } else if (key === 'c' || key === 'escape') {
        ev.preventDefault();
        clearMarkers();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enableMarkers, isPlaying, clearMarkers]);

  /* ── Keyboard: '['/']' step speed; '0' resets to 1× ─────────── */
  useEffect(() => {
    if (!enableSpeed) return;
    if (!isPlaying) return; // Only the actively-playing track owns the keyboard.
    const onKey = (ev: KeyboardEvent) => {
      const tgt = ev.target as HTMLElement | null;
      const tag = (tgt?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (tgt && tgt.isContentEditable)) return;
      const key = ev.key;
      if (key === '[') {
        ev.preventDefault();
        setSpeed((s) => stepPlaybackRate(s, -1));
      } else if (key === ']') {
        ev.preventDefault();
        setSpeed((s) => stepPlaybackRate(s, +1));
      } else if (key === '0') {
        ev.preventDefault();
        setSpeed(1.0);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enableSpeed, isPlaying]);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const tgt = ev.target as HTMLElement | null;
      const tag = (tgt?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (tgt && tgt.isContentEditable)) return;

      const root = playerRootRef.current;
      const active = document.activeElement as HTMLElement | null;
      const focusInside = !!(root && active && root.contains(active));
      if (!focusInside) return;

      // While speed mode is active during playback, reserve [ / ] for rate control.
      const speedOwnsBracketKeys = enableSpeed && isPlaying;

      if (!speedOwnsBracketKeys && (ev.key === '[' || ev.key === '-')) {
        ev.preventDefault();
        shiftWaveZoom(-1);
      } else if (!speedOwnsBracketKeys && (ev.key === ']' || ev.key === '=' || ev.key === '+')) {
        ev.preventDefault();
        shiftWaveZoom(1);
      } else if (ev.key.toLowerCase() === 'f') {
        ev.preventDefault();
        fitWaveSelection();
      } else if (ev.key === '0' && (ev.metaKey || ev.ctrlKey)) {
        ev.preventDefault();
        setZoomAround(getZoomAnchorSec(), 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enableSpeed, fitWaveSelection, getZoomAnchorSec, isPlaying, setZoomAround, shiftWaveZoom]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current != null) window.clearTimeout(copyTimerRef.current);
      if (dragPreviewRafRef.current != null) window.cancelAnimationFrame(dragPreviewRafRef.current);
      if (panRafRef.current != null) window.cancelAnimationFrame(panRafRef.current);
      if (hoverUiRafRef.current != null) window.cancelAnimationFrame(hoverUiRafRef.current);
    };
  }, []);

  async function copyToClipboard(text: string) {
    const s = String(text ?? '').trim();
    if (!s) return;

    function bump(next: 'copied' | 'error') {
      setCopyState(next);
      if (copyTimerRef.current != null) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => {
        setCopyState('idle');
        copyTimerRef.current = null;
      }, 1300);
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(s);
        bump('copied');
        return;
      }
      const ta = document.createElement('textarea');
      ta.value = s;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      bump(ok ? 'copied' : 'error');
    } catch {
      bump('error');
    }
  }

  const togglePlayCore = useCallback((allowTripleTap: boolean) => {
    const el = audioRef.current;
    if (!el) return;

    // If currently looping, single click exits loop and stops.
    if (looping) {
      el.pause();
      setIsPlaying(false);
      setLooping(false);
      tapTimesRef.current = [];
      if (activeAudioEl === el) activeAudioEl = null;
      lastActiveAudioEl = el;
      return;
    }

    if (allowTripleTap) {
      // Track tap timestamps for triple-tap gesture (750ms sliding window).
      // 750ms gives comfortable margin for the play→stop→play visual cycle
      // that naturally adds ~100-200ms hesitation on the middle tap.
      const now = Date.now();
      tapTimesRef.current = tapTimesRef.current.filter((t) => now - t < 750);
      tapTimesRef.current.push(now);

      // Triple-tap detected → activate loop mode and start playing from 0.
      if (tapTimesRef.current.length >= 3) {
        tapTimesRef.current = [];
        if (activeAudioEl && activeAudioEl !== el) {
          try { activeAudioEl.pause(); } catch {}
          try { activeAudioEl.currentTime = 0; } catch {}
        }
        activeAudioEl = el;
        lastActiveAudioEl = el;
        try { el.currentTime = 0; } catch {}
        setCurrentTime(0);
        tryPlay(el);
        setLooping(true);
        return;
      }
    }

    // Normal behavior (non-looping):
    if (el.paused) {
      if (activeAudioEl && activeAudioEl !== el) {
        try { activeAudioEl.pause(); } catch {}
        try { activeAudioEl.currentTime = 0; } catch {}
      }
      activeAudioEl = el;
      lastActiveAudioEl = el;

      // Spacebar/keyboard (allowTripleTap=false): resume from current position (DAW-standard)
      // Mouse click (allowTripleTap=true): start from selection or 0
      if (!allowTripleTap) {
        // Keyboard spacebar: resume from current paused position
        tryPlay(el);
      } else {
        // Mouse click: start from selection start or 0
        const startAt = dragLoopRegion ? dragLoopRegion[0] : 0;
        try { el.currentTime = startAt; } catch {}
        setCurrentTime(startAt);
        tryPlay(el);
      }
    } else {
      el.pause();
      setIsPlaying(false);
      if (activeAudioEl === el) activeAudioEl = null;
      lastActiveAudioEl = el;
    }
  }, [looping, dragLoopRegion]);

  const togglePlay = useCallback(() => {
    togglePlayCore(true);
  }, [togglePlayCore]);

  const togglePlayFromKeyboard = useCallback(() => {
    togglePlayCore(false);
  }, [togglePlayCore]);

  const clearDragLoop = useCallback(() => {
    setDragLoopRegion(null);
    setDragPreviewRegion(null);
  }, []);

  const scheduleDragPreview = useCallback((region: [number, number] | null) => {
    dragPreviewNextRef.current = region;
    if (dragPreviewRafRef.current != null) return;
    dragPreviewRafRef.current = window.requestAnimationFrame(() => {
      dragPreviewRafRef.current = null;
      setDragPreviewRegion(dragPreviewNextRef.current);
    });
  }, []);

  const schedulePanTime = useCallback((nextTime: number) => {
    panNextTimeRef.current = nextTime;
    if (panRafRef.current != null) return;
    panRafRef.current = window.requestAnimationFrame(() => {
      panRafRef.current = null;
      const target = panNextTimeRef.current;
      if (target != null) setCurrentTime(target);
    });
  }, []);

  const seekTimeFromClientX = useCallback((clientX: number): number | null => {
    const track = trackRef.current;
    if (!track || !duration || !Number.isFinite(duration) || duration <= 0) return null;
    const rect = track.getBoundingClientRect();
    const x = clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / Math.max(1, rect.width)));
    return zoomWindow.start + ratio * zoomWindow.span;
  }, [duration, zoomWindow]);

  const timeToXInTrack = useCallback((t: number): number | null => {
    const track = trackRef.current;
    if (!track || !duration || !Number.isFinite(duration) || duration <= 0) return null;
    const W = track.getBoundingClientRect().width;
    if (t < zoomWindow.start || t > zoomWindow.end) return null;
    const ratio = (t - zoomWindow.start) / Math.max(1, zoomWindow.span);
    return ratio * W;
  }, [duration, zoomWindow]);

  /** Detect which handle (left/right) is near clientX, if any. Returns 'left' | 'right' | null. */
  const getHandleAtClientX = useCallback((clientX: number): 'left' | 'right' | null => {
    if (!dragLoopRegion || !duration || !Number.isFinite(duration) || duration <= 0) return null;
    const [lo, hi] = dragLoopRegion;
    const xLo = timeToXInTrack(lo);
    const xHi = timeToXInTrack(hi);
    if (xLo == null || xHi == null) return null;

    const track = trackRef.current;
    if (!track) return null;
    const rect = track.getBoundingClientRect();
    const relX = clientX - rect.left;
    const hitDist = handleHitDistanceRef.current;

    // Check left handle first
    if (Math.abs(relX - xLo) <= hitDist) return 'left';
    // Check right handle
    if (Math.abs(relX - xHi) <= hitDist) return 'right';
    return null;
  }, [dragLoopRegion, duration, timeToXInTrack]);

  const onTrackMouseMove = useCallback((ev: React.MouseEvent<HTMLDivElement>) => {
    const sec = seekTimeFromClientX(ev.clientX);
    if (sec == null) return;
    hoverAnchorSecRef.current = sec;

    const track = trackRef.current;
    if (!track) return;

    // Check if hovering near a handle and update cursor accordingly
    const nearHandle = getHandleAtClientX(ev.clientX);
    track.style.cursor = nearHandle ? 'ew-resize' : 'default';

    const rect = track.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, ev.clientX - rect.left));
    hoverUiNextRef.current = { x, sec };
    if (hoverUiRafRef.current != null) return;
    hoverUiRafRef.current = window.requestAnimationFrame(() => {
      hoverUiRafRef.current = null;
      setHoverTimeUi(hoverUiNextRef.current);
    });
  }, [seekTimeFromClientX, getHandleAtClientX]);

  const onTrackMouseLeave = useCallback(() => {
    const track = trackRef.current;
    if (track) track.style.cursor = 'default';
    hoverAnchorSecRef.current = null;
    hoverUiNextRef.current = null;
    if (hoverUiRafRef.current != null) {
      window.cancelAnimationFrame(hoverUiRafRef.current);
      hoverUiRafRef.current = null;
    }
    setHoverTimeUi(null);
  }, []);

  const commitDragLoop = useCallback((a: number, b: number) => {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    if (hi - lo < 0.02) {
      setDragLoopRegion(null);
      setDragPreviewRegion(null);
      return;
    }
    setDragLoopRegion([lo, hi]);
    setDragPreviewRegion(null);
    const el = audioRef.current;
    if (el) {
      try { el.currentTime = lo; } catch {}
    }
    setCurrentTime(lo);
  }, []);

  const onTrackMouseDown = useCallback((ev: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    const t0 = seekTimeFromClientX(ev.clientX);
    if (!el || t0 == null || !isReady || hasError) return;

    const snapEnabled = dragSnapMode === 'beat' && enableBeatGrid && beats.length > 0;
    const nearHandle = getHandleAtClientX(ev.clientX);

    // Handle edge resizing
    if (nearHandle && dragLoopRegion) {
      resizeEdgeRef.current = nearHandle;
      dragStartXRef.current = ev.clientX;
      dragStartSecRef.current = t0;
      dragMovedRef.current = false;
      const [lo, hi] = dragLoopRegion;

      const onMove = (e: MouseEvent) => {
        if (resizeEdgeRef.current == null || dragLoopRegion == null) return;
        const t = seekTimeFromClientX(e.clientX);
        if (t == null) return;
        const snappedT = snapToNearestBeat(t, duration, beats, snapEnabled);
        
        if (Math.abs(e.clientX - dragStartXRef.current!) >= 4) {
          dragMovedRef.current = true;
        }

        const [origLo, origHi] = dragLoopRegion;
        const newRegion: [number, number] = resizeEdgeRef.current === 'left'
          ? [Math.min(snappedT, origHi - 0.02), origHi]
          : [origLo, Math.max(snappedT, origLo + 0.02)];
        
        scheduleDragPreview(newRegion);
      };

      const onUp = (e: MouseEvent) => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        
        if (!dragMovedRef.current || !dragLoopRegion) {
          scheduleDragPreview(null);
          dragMovedRef.current = false;
          resizeEdgeRef.current = null;
          return;
        }

        const t = seekTimeFromClientX(e.clientX);
        const snappedT = t == null ? null : snapToNearestBeat(t, duration, beats, snapEnabled);
        if (snappedT == null) {
          scheduleDragPreview(null);
          dragMovedRef.current = false;
          resizeEdgeRef.current = null;
          return;
        }

        const [origLo, origHi] = dragLoopRegion;
        const newRegion: [number, number] = resizeEdgeRef.current === 'left'
          ? [Math.min(snappedT, origHi - 0.02), origHi]
          : [origLo, Math.max(snappedT, origLo + 0.02)];

        suppressClickSeekRef.current = true;
        setDragLoopRegion(newRegion);
        setDragPreviewRegion(null);
        dragMovedRef.current = false;
        resizeEdgeRef.current = null;
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return;
    }

    // Regular drag-to-select (existing behavior)
    const startT = snapToNearestBeat(t0, duration, beats, snapEnabled);
    dragStartXRef.current = ev.clientX;
    dragStartSecRef.current = startT;
    dragMovedRef.current = false;
    scheduleDragPreview([startT, startT]);

    const onMove = (e: MouseEvent) => {
      if (dragStartXRef.current == null || dragStartSecRef.current == null) return;
      const t = seekTimeFromClientX(e.clientX);
      if (t == null) return;
      const snappedT = snapToNearestBeat(t, duration, beats, snapEnabled);
      if (Math.abs(e.clientX - dragStartXRef.current) >= 4) {
        dragMovedRef.current = true;
      }
      scheduleDragPreview([
        Math.min(dragStartSecRef.current, snappedT),
        Math.max(dragStartSecRef.current, snappedT),
      ]);
    };

    const onUp = (e: MouseEvent) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const startSec = dragStartSecRef.current;
      dragStartXRef.current = null;
      dragStartSecRef.current = null;

      const t = seekTimeFromClientX(e.clientX);
      const endSec = t == null
        ? null
        : snapToNearestBeat(t, duration, beats, snapEnabled);

      if (startSec == null || endSec == null) {
        scheduleDragPreview(null);
        dragMovedRef.current = false;
        return;
      }

      if (dragMovedRef.current) {
        suppressClickSeekRef.current = true;
        commitDragLoop(startSec, endSec);
      } else {
        scheduleDragPreview(null);
      }
      dragMovedRef.current = false;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [beats, commitDragLoop, dragLoopRegion, dragSnapMode, duration, enableBeatGrid, getHandleAtClientX, hasError, isReady, scheduleDragPreview, seekTimeFromClientX]);

  function seekFromClick(ev: React.MouseEvent<HTMLDivElement>) {
    if (suppressClickSeekRef.current) {
      suppressClickSeekRef.current = false;
      return;
    }
    const el = audioRef.current;
    const track = trackRef.current;
    if (!el || !track || !duration || !isReady || hasError) return;
    const rect = track.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / Math.max(1, rect.width)));
    el.currentTime = zoomWindow.start + ratio * zoomWindow.span;
  }

  const onTrackWheel = useCallback((ev: React.WheelEvent<HTMLDivElement>) => {
    if (!duration || !Number.isFinite(duration) || duration <= 0 || !isReady || hasError) return;
    const track = trackRef.current;
    if (!track) return;

    const rect = track.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / Math.max(1, rect.width)));
    const anchor = zoomWindow.start + ratio * zoomWindow.span;

    // Mac trackpad pinch typically arrives as wheel+ctrlKey.
    const wantsZoom = ev.ctrlKey || ev.metaKey;
    if (wantsZoom) {
      ev.preventDefault();
      const idx = Math.max(0, WAVE_ZOOM_STOPS.indexOf(waveZoom as (typeof WAVE_ZOOM_STOPS)[number]));
      const dir: -1 | 1 = ev.deltaY < 0 ? 1 : -1;
      const nextIdx = Math.max(0, Math.min(WAVE_ZOOM_STOPS.length - 1, idx + dir));
      setZoomAround(anchor, WAVE_ZOOM_STOPS[nextIdx]);
      return;
    }

    // Two-finger pan while zoomed in (trackpad-friendly).
    if (waveZoom > 1) {
      const delta = Math.abs(ev.deltaX) > Math.abs(ev.deltaY) ? ev.deltaX : ev.deltaY;
      if (Math.abs(delta) > 0.1) {
        ev.preventDefault();
        const span = Math.max(1e-6, zoomWindow.span);
        const next = currentTime + delta * (span / 420);
        schedulePanTime(Math.max(0, Math.min(duration, next)));
      }
    }
  }, [duration, isReady, hasError, zoomWindow, waveZoom, currentTime, schedulePanTime, setZoomAround]);

  const onTrackKeyDown = useCallback((ev: React.KeyboardEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el || !duration || !isReady || hasError) return;
    // Spacebar is handled globally by window.addEventListener, not here
    if (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight') {
      ev.preventDefault();
      const step = ev.shiftKey ? 5 : 1;
      const sign = ev.key === 'ArrowRight' ? 1 : -1;
      const next = Math.max(0, Math.min(duration, (el.currentTime || 0) + sign * step));
      try { el.currentTime = next; } catch {}
      setCurrentTime(next);
    }
  }, [duration, hasError, isReady]);

  const onTrackFocus = useCallback(() => {
    setTrackHasFocus(true);
  }, []);

  const onTrackBlur = useCallback(() => {
    setTrackHasFocus(false);
  }, []);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (!(ev.key === ' ' || ev.key === 'Spacebar')) return;
      const tgt = ev.target as HTMLElement | null;
      const tag = (tgt?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (tgt && tgt.isContentEditable)) return;

      const own = audioRef.current;
      if (!own || hasError || !src) return;

      // Global spacebar: find the target player (active or last active)
      const targetPlayer = activeAudioEl ?? lastActiveAudioEl ?? own;
      if (!targetPlayer) return;

      // Only handle spacebar if this component owns the target player
      if (targetPlayer !== own) return;

      ev.preventDefault();

      // DAW-standard behavior: pause/resume
      if (!targetPlayer.paused) {
        // Playing → pause
        targetPlayer.pause();
        setIsPlaying(false);
        if (activeAudioEl === targetPlayer) activeAudioEl = null;
        lastActiveAudioEl = targetPlayer;
        return;
      }

      // Paused → resume from current position
      activeAudioEl = targetPlayer;
      lastActiveAudioEl = targetPlayer;
      tryPlay(targetPlayer);
      setIsPlaying(true);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasError, src]);

  const actionBtnCommon = 'rounded border bg-transparent hover:border-neutral-500 text-[11px] leading-none cursor-default';

  const copyLabel = copyState === 'copied' ? 'Copied' : (copyState === 'error' ? 'Copy failed' : 'Copy');
  const copyClassName =
    `h-6 px-2 ${actionBtnCommon} whitespace-nowrap `
    + (copyState === 'copied'
      ? 'text-emerald-300 border-emerald-900/40'
      : (copyState === 'error'
        ? 'text-rose-300 border-rose-900/40'
        : 'text-neutral-400 border-neutral-800'));

  /* Playback button icon + aria label based on state. */
  const playbackIcon = useMemo(() => {
    if (isPlaying) return <Pause className="h-3.5 w-3.5 mx-auto" />;
    return <Play className="h-3.5 w-3.5 mx-auto" />;
  }, [isPlaying]);

  const playbackAriaLabel = isPlaying ? 'Stop audio' : 'Play audio';

  const playbackBtnClass = useMemo(() => {
    return `h-6 w-6 ${actionBtnCommon} border-neutral-800 text-neutral-300`;
  }, [actionBtnCommon]);

  return (
    <div ref={playerRootRef} className="w-full" title={gsUri}>
      <div className="flex items-center gap-2 w-full">
        <button
          type="button"
          className={playbackBtnClass}
          onClick={togglePlay}
          aria-label={playbackAriaLabel}
          disabled={!src || hasError}
        >
          {playbackIcon}
        </button>

        {sidecarLabel && (
          <span className="text-[10px] text-neutral-500 truncate max-w-[140px]" title={sidecarLabel}>
            {sidecarLabel}
          </span>
        )}

        <div
          ref={trackRef}
          role="slider"
          aria-label="Seek audio"
          aria-keyshortcuts="Space ArrowLeft ArrowRight"
          aria-valuemin={0}
          aria-valuemax={duration ?? 0}
          aria-valuenow={Math.min(duration ?? 0, currentTime)}
          tabIndex={0}
          style={{ touchAction: 'none', userSelect: 'none' }}
          onMouseMove={onTrackMouseMove}
          onMouseLeave={onTrackMouseLeave}
          onFocus={onTrackFocus}
          onBlur={onTrackBlur}
          onKeyDown={onTrackKeyDown}
          onMouseDown={onTrackMouseDown}
          onClick={seekFromClick}
          onWheel={onTrackWheel}
          className={
            `flex-1 ${tall ? 'h-40' : 'h-14'} rounded border overflow-hidden relative outline-none focus-visible:ring-1 focus-visible:ring-neutral-500 ` +
            (isReady && !hasError && duration
              ? 'border-neutral-700 bg-transparent cursor-default'
              : 'border-neutral-800 bg-transparent cursor-default')
          }
        >
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
          {hoverTimeUi && (
            <div
              className="absolute top-0 pointer-events-none -translate-x-1/2 px-1 py-[1px] rounded bg-black/85 border border-neutral-700 text-[10px] leading-none font-mono text-neutral-200"
              style={{ left: `${hoverTimeUi.x}px` }}
            >
              {formatDurationSeconds(hoverTimeUi.sec)}
            </div>
          )}
        </div>

        <div className="text-[11px] font-mono text-neutral-500 whitespace-nowrap tabular-nums">{remainingLabel}</div>

        <button
          type="button"
          className={copyClassName}
          onClick={() => void copyToClipboard(gsUri)}
          aria-label="Copy audio artifact URI"
          disabled={!gsUri}
        >
          {copyLabel}
        </button>

        <audio
          ref={handleAudioRef}
          preload="metadata"
          src={src}
        />
      </div>

      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {playbackStatusLabel}
      </div>

      {/* Marker / loop-region + speed + beat-grid footer (only when feature(s) enabled) */}
      {(enableMarkers || enableSpeed || enableBeatGrid || !!dragLoopRegion) && (
        <div className="mt-1 ml-8 flex items-center gap-3 text-[10px] font-mono">
          <span className={isPlaying ? 'text-emerald-400' : 'text-neutral-500'} title={playbackStatusLabel}>
            {playbackStatusLabel}
          </span>
          {enableMarkers && markerIn != null && (
            <span className="text-cyan-400">
              IN&nbsp;{formatDurationSeconds(markerIn)}
            </span>
          )}
          {enableMarkers && markerOut != null && (
            <span className="text-amber-400">
              OUT&nbsp;{formatDurationSeconds(markerOut)}
            </span>
          )}
          {enableMarkers && loopRegion && (
            <span className="text-violet-400">
              LOOP&nbsp;{formatDurationSeconds(loopRegion[1] - loopRegion[0])}
            </span>
          )}
          {dragLoopRegion && (
            <span className="text-emerald-400" title="Drag-selected playback range (snaps to beats when beat grid is available)">
              SEL&nbsp;{formatDurationSeconds(dragLoopRegion[1] - dragLoopRegion[0])}
            </span>
          )}
          {enableMarkers && (markerIn != null || markerOut != null) && (
            <button
              type="button"
              onClick={clearMarkers}
              className="text-neutral-500 hover:text-neutral-300 underline-offset-2 hover:underline cursor-default"
              title="Clear markers (c / Esc)"
            >
              clear
            </button>
          )}
          {dragLoopRegion && (
            <button
              type="button"
              onClick={clearDragLoop}
              className="text-emerald-500 hover:text-emerald-300 underline-offset-2 hover:underline cursor-default"
              title="Clear drag-selected playback range"
            >
              clear selection
            </button>
          )}

          <div className="flex items-center gap-1">
            <span className="text-neutral-600">select</span>
            <button
              type="button"
              onClick={() => setDragSnapMode('beat')}
              className={
                'px-1.5 py-0.5 rounded border cursor-default tabular-nums ' +
                (dragSnapMode === 'beat'
                  ? 'border-neutral-600 bg-neutral-800/70 text-neutral-100'
                  : 'border-neutral-800 bg-transparent text-neutral-500 hover:border-neutral-700 hover:text-neutral-300')
              }
              title="Stick selection edges to nearest beat timestamp"
            >
              beat
            </button>
            <button
              type="button"
              onClick={() => setDragSnapMode('free')}
              className={
                'px-1.5 py-0.5 rounded border cursor-default tabular-nums ' +
                (dragSnapMode === 'free'
                  ? 'border-neutral-600 bg-neutral-800/70 text-neutral-100'
                  : 'border-neutral-800 bg-transparent text-neutral-500 hover:border-neutral-700 hover:text-neutral-300')
              }
              title="Free continuous selection without beat snapping"
            >
              free
            </button>
          </div>

          <div className="flex items-center gap-1">
            <span className="text-neutral-600">zoom</span>
            <button
              type="button"
              onClick={() => shiftWaveZoom(-1)}
              className="px-1.5 py-0.5 rounded border border-neutral-800 bg-transparent text-neutral-400 hover:border-neutral-600 hover:text-neutral-200 cursor-default"
              title="Zoom out waveform"
              disabled={waveZoom <= WAVE_ZOOM_STOPS[0]}
            >
              -
            </button>
            {WAVE_ZOOM_STOPS.map((z) => {
              const active = waveZoom === z;
              return (
                <button
                  key={z}
                  type="button"
                  onClick={() => setWaveZoom(z)}
                  className={
                    'px-1.5 py-0.5 rounded border cursor-default tabular-nums ' +
                    (active
                      ? 'border-neutral-600 bg-neutral-800/70 text-neutral-100'
                      : 'border-neutral-800 bg-transparent text-neutral-500 hover:border-neutral-700 hover:text-neutral-300')
                  }
                  title={`Set waveform zoom to ${z}x`}
                >
                  {z}x
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => shiftWaveZoom(1)}
              className="px-1.5 py-0.5 rounded border border-neutral-800 bg-transparent text-neutral-400 hover:border-neutral-600 hover:text-neutral-200 cursor-default"
              title="Zoom in waveform"
              disabled={waveZoom >= WAVE_ZOOM_STOPS[WAVE_ZOOM_STOPS.length - 1]}
            >
              +
            </button>
            <button
              type="button"
              onClick={() => fitWaveSelection()}
              className="px-1.5 py-0.5 rounded border border-neutral-800 bg-transparent text-neutral-400 hover:border-neutral-600 hover:text-neutral-200 cursor-default"
              title="Fit selected region or reset to full waveform (F)"
            >
              fit
            </button>
          </div>

          {trackHasFocus && (
            <span className="text-neutral-500" title="Keyboard: Space play/stop · Arrow keys nudge · [ ] zoom · F fit selection">
              keys: space, ←/→, shift+←/→, [ ], f
            </span>
          )}

          {enableBeatGrid && hasBeatGrid && (
            <span className="text-violet-400" title={`Beat grid: ${sidecar?.analysis?.beat_grid?.total_beats ?? beats.length} beats / ${sidecar?.analysis?.beat_grid?.total_downbeats ?? '—'} downbeats`}>
              BEATS&nbsp;{sidecar?.analysis?.beat_grid?.total_beats ?? beats.length}
            </span>
          )}
          {enableBeatGrid && !hasBeatGrid && scopeKey && (
            // When auto-analysis is in-flight, show a passive pulsing indicator
            // instead of the manual detect-beats button.
            analysisInProgress ? (
              <span
                className="animate-pulse flex items-center gap-1 text-[10px] text-amber-500"
                title="BPM + beat analysis dispatched automatically — results will appear here shortly"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                analyzing…
              </span>
            ) : (
              <button
                type="button"
                onClick={() => void requestBeatDetection()}
                disabled={isAnalyzing || !src || hasError}
                className={
                  'px-1.5 py-0.5 rounded border cursor-default tabular-nums ' +
                  (isAnalyzing
                    ? 'border-violet-800/60 bg-violet-900/20 text-violet-300 animate-pulse'
                    : 'border-neutral-800 bg-transparent text-neutral-400 hover:border-violet-600 hover:text-violet-300')
                }
                title="Run BeatNet beat / downbeat detection on this track"
              >
                {isAnalyzing ? 'detecting…' : 'detect beats'}
              </button>
            )
          )}
          {enableBeatGrid && analyzeError && !isAnalyzing && (
            <button
              type="button"
              onClick={() => { setAnalyzeError(null); void requestBeatDetection(); }}
              className="px-1.5 py-0.5 rounded border border-rose-700/60 bg-rose-900/20 text-rose-300 hover:border-rose-500 hover:text-rose-200 cursor-default"
              title={analyzeError}
            >
              retry detection
            </button>
          )}

          {enableSpeed && (
            <div className="ml-auto flex items-center gap-1">
              <span className="text-neutral-600">speed</span>
              {PLAYBACK_RATE_STOPS.map((r) => {
                const active = Math.abs(speed - r) < 1e-3;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setSpeed(clampPlaybackRate(r))}
                    className={
                      'px-1.5 py-0.5 rounded border cursor-default tabular-nums ' +
                      (active
                        ? 'border-emerald-700/60 bg-emerald-900/20 text-emerald-300'
                        : 'border-neutral-800 bg-transparent text-neutral-400 hover:border-neutral-600 hover:text-neutral-200')
                    }
                    title={`Set playback speed ${r}\u00d7`}
                  >
                    {r === 1 ? '1\u00d7' : `${r}\u00d7`}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Beat-detection live progress card — sourced from /bff/orchestrator/runs/:id/stream.
          Surfaces percent, current step, attempts, last_error, elapsed time so the user
          knows exactly what the pipeline is doing instead of staring at a generic spinner. */}
      {enableBeatGrid && (isAnalyzing || analyzeError) && (
        <BeatDetectionProgressCard
          progress={analyzeProgress}
          isAnalyzing={isAnalyzing}
          error={analyzeError}
          onRetry={() => { setAnalyzeError(null); void requestBeatDetection(); }}
          onDismiss={() => { setAnalyzeError(null); setAnalyzeProgress(null); }}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * BeatDetectionProgressCard
 * Renders a compact, live-updating panel for the on-demand BeatNet run.
 * Shows percent bar, per-step status with attempts + last_error, elapsed
 * time, and stream-health hint. Designed to live directly under the player.
 * ────────────────────────────────────────────────────────────────────── */
type BeatDetectionStep = {
  step_id: string;
  status: string;
  attempts?: number | null;
  last_error?: string | null;
  progress?: { percent?: number | null; message?: string | null } | null;
  started_at?: string | null;
  finished_at?: string | null;
  duration_ms?: number | null;
};

type BeatDetectionProgressShape = {
  runId: string;
  status: string;
  percent: number;
  counts: { total: number; succeeded: number; failed: number; running: number; pending: number; ready: number };
  steps: BeatDetectionStep[];
  startedAt: number;
  streamError?: string | null;
};

function BeatDetectionProgressCard({
  progress,
  isAnalyzing,
  error,
  onRetry,
  onDismiss,
}: {
  progress: BeatDetectionProgressShape | null;
  isAnalyzing: boolean;
  error: string | null;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  // Tick once a second so elapsed time refreshes even when no SSE event arrives.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (!isAnalyzing) return;
    const id = window.setInterval(() => setNowTick((n) => (n + 1) % 1_000_000), 1_000);
    return () => window.clearInterval(id);
  }, [isAnalyzing]);

  const status = String(progress?.status ?? (isAnalyzing ? 'PENDING' : '')).toUpperCase();
  const isFailed = !!error || status === 'FAILED' || status === 'ERROR' || (progress?.counts?.failed ?? 0) > 0;
  const percent = Math.max(0, Math.min(100, Math.round(progress?.percent ?? 0)));
  const elapsedMs = progress?.startedAt ? Date.now() - progress.startedAt : 0;
  const elapsedLabel = formatElapsed(elapsedMs);

  const steps = progress?.steps ?? [];
  const activeStep =
    steps.find((s) => String(s.status).toUpperCase() === 'RUNNING') ??
    steps.find((s) => String(s.status).toUpperCase() === 'FAILED' || String(s.status).toUpperCase() === 'ERROR') ??
    steps.find((s) => String(s.status).toUpperCase() === 'READY') ??
    steps[steps.length - 1] ?? null;

  // Color tokens aligned with the rest of the dev UI (PAC Glass).
  const accent = isFailed ? 'rose' : status === 'SUCCEEDED' ? 'emerald' : 'violet';
  const barClass =
    accent === 'rose'    ? 'bg-rose-500/70'
    : accent === 'emerald' ? 'bg-emerald-500/70'
    : 'bg-violet-500/70';
  const borderClass =
    accent === 'rose'    ? 'border-rose-800/50'
    : accent === 'emerald' ? 'border-emerald-800/50'
    : 'border-violet-800/40';

  return (
    <div className={`mt-2 ml-8 mr-2 rounded border ${borderClass} bg-neutral-900/40 p-2.5 text-[11px] font-mono space-y-1.5`}
         role="status" aria-live="polite">
      <div className="flex items-center gap-2">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${
          isFailed ? 'bg-rose-400'
          : status === 'SUCCEEDED' ? 'bg-emerald-400'
          : 'bg-violet-400 animate-pulse'
        }`} />
        <span className="text-neutral-200 font-semibold">
          {isFailed ? 'Beat detection failed' : status === 'SUCCEEDED' ? 'Beat detection complete' : 'Detecting beats…'}
        </span>
        {progress?.runId && (
          <span className="text-neutral-600 text-[10px]" title="Orchestrator run id">
            run #{progress.runId}
          </span>
        )}
        <span className="ml-auto text-neutral-500 tabular-nums">{elapsedLabel}</span>
        {!isAnalyzing && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-neutral-600 hover:text-neutral-300 cursor-default"
            title="Dismiss"
            aria-label="Dismiss progress card"
          >
            ✕
          </button>
        )}
      </div>

      {/* Percent bar */}
      <div className="relative h-1 w-full rounded bg-neutral-800/80 overflow-hidden">
        <div
          className={`absolute left-0 top-0 h-full ${barClass} transition-all duration-300 ease-out`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="flex items-center gap-3 text-neutral-500">
        <span className="tabular-nums">{percent}%</span>
        {progress?.counts?.total != null && progress.counts.total > 0 && (
          <span className="tabular-nums">
            {progress.counts.succeeded}/{progress.counts.total} steps
          </span>
        )}
        {progress?.counts?.failed != null && progress.counts.failed > 0 && (
          <span className="text-rose-400 tabular-nums">{progress.counts.failed} failed</span>
        )}
        {progress?.streamError && (
          <span className="text-amber-400 truncate" title={progress.streamError}>
            {progress.streamError}
          </span>
        )}
      </div>

      {/* Active step + per-step list */}
      {activeStep && (
        <div className="text-neutral-400">
          <span className="text-neutral-500">current:&nbsp;</span>
          <span className="text-neutral-200">{activeStep.step_id}</span>
          <span className="ml-2 text-neutral-600">{String(activeStep.status).toLowerCase()}</span>
          {typeof activeStep.attempts === 'number' && activeStep.attempts > 1 && (
            <span className="ml-2 text-amber-400">attempt {activeStep.attempts}</span>
          )}
          {activeStep.progress?.percent != null && (
            <span className="ml-2 text-neutral-500 tabular-nums">{Math.round(activeStep.progress.percent)}%</span>
          )}
          {activeStep.progress?.message && (
            <span className="ml-2 text-neutral-500 truncate">— {activeStep.progress.message}</span>
          )}
        </div>
      )}

      {steps.length > 1 && (
        <ul className="space-y-0.5 max-h-24 overflow-y-auto pr-1">
          {steps.map((s) => {
            const st = String(s.status).toUpperCase();
            const k =
              st === 'SUCCEEDED' ? { dot: 'bg-emerald-400', text: 'text-emerald-400' }
              : st === 'FAILED' || st === 'ERROR' ? { dot: 'bg-rose-400', text: 'text-rose-400' }
              : st === 'RUNNING' ? { dot: 'bg-amber-300 animate-pulse', text: 'text-amber-300' }
              : { dot: 'bg-neutral-600', text: 'text-neutral-500' };
            return (
              <li key={s.step_id} className={`flex items-center gap-1.5 ${k.text}`}>
                <span className={`inline-block w-1 h-1 rounded-full ${k.dot}`} />
                <span className="text-neutral-300">{s.step_id}</span>
                <span>{st.toLowerCase()}</span>
                {typeof s.attempts === 'number' && s.attempts > 0 && (
                  <span className="text-neutral-600">(attempt {s.attempts})</span>
                )}
                {s.last_error && (
                  <span className="text-rose-400 truncate" title={s.last_error}>— {s.last_error}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Detailed error footer + retry */}
      {error && (
        <div className="border-t border-rose-900/50 pt-1.5 mt-1 flex items-start gap-2">
          <span className="text-rose-400 shrink-0">error:</span>
          <span className="text-rose-300 break-words flex-1">{error}</span>
          <button
            type="button"
            onClick={onRetry}
            className="px-2 py-0.5 rounded border border-rose-700/60 bg-rose-900/30 text-rose-200 hover:border-rose-500 hover:text-rose-100 cursor-default shrink-0"
          >
            retry
          </button>
        </div>
      )}
    </div>
  );
}

function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0s';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m ${String(rem).padStart(2, '0')}s`;
}
